import { z } from "zod/v4";

/**
 * 24h stats + current price snapshot for one spot market — the spot
 * counterpart of the perp asset contexts. Perp-only concepts (funding, open
 * interest, mark/oracle prices) are deliberately absent.
 *
 * Null semantics: `last_price`/`high_24h`/`low_24h` are null when the market
 * had no trades in the last 24h; `mid` is null unless both book sides have
 * resting liquidity; `prev_day_price` is null for markets that never traded
 * before the 24h boundary (render 24h change as n/a). 24h change =
 * (last_price - prev_day_price) / prev_day_price, derived client-side.
 */
export const SpotAssetContextSchema = z.object({
  market_addr: z.string(),
  name: z.string(),
  ticker_id: z.string(),
  base_asset_addr: z.string(),
  quote_asset_addr: z.string(),
  base_decimals: z.number(),
  quote_decimals: z.number(),
  last_price: z.number().nullable(),
  mid: z.number().nullable(),
  prev_day_price: z.number().nullable(),
  volume_24h_base: z.number(),
  volume_24h_quote: z.number(),
  high_24h: z.number().nullable(),
  low_24h: z.number().nullable(),
  timestamp_unix_ms: z.number(),
});

export const SpotAssetContextsSchema = z.array(SpotAssetContextSchema);

export type SpotAssetContext = z.infer<typeof SpotAssetContextSchema>;
export type SpotAssetContexts = z.infer<typeof SpotAssetContextsSchema>;
